import {
  SignalWifi1Bar,
  SignalWifi2Bar,
  SignalWifi3Bar,
  SignalWifi4Bar,
  SignalWifiOff,
  SignalWifiStatusbarConnectedNoInternet4,
  type SvgIconComponent
} from '@mui/icons-material';
import type { Socket } from 'socket.io-client';
import {
  ConnectionQuality,
  ConnectionQualityState,
  SignalLevel
} from '../../types/connectionQuality';

const PING_INTERVAL_MS = 5000;
const PING_TIMEOUT_MS = 8000;
const FAILED_PINGS_BEFORE_UNSTABLE = 3;

export const CONNECTED_SIGNAL_COLOR = '#ffffff';
export const OFFLINE_SIGNAL_COLOR = '#ffffff';
export const RECONNECTING_SIGNAL_COLOR = '#ffffff';
export const UNSTABLE_SIGNAL_COLOR = '#ffffff';

export const RECONNECTING_SIGNAL_LEVELS: SignalLevel[] = [1, 2, 3, 4];

export const OFFLINE_SIGNAL_ICON = SignalWifiOff;
export const UNSTABLE_SIGNAL_ICON = SignalWifiStatusbarConnectedNoInternet4;

const SIGNAL_ICONS: Record<SignalLevel, SvgIconComponent> = {
  4: SignalWifi4Bar,
  3: SignalWifi3Bar,
  2: SignalWifi2Bar,
  1: SignalWifi1Bar
};

export const INITIAL_CONNECTION_QUALITY_STATE: ConnectionQualityState = {
  quality: 'offline',
  signalLevel: 1,
  signalColor: OFFLINE_SIGNAL_COLOR,
  rttMs: null,
  lastMeasuredAt: null,
  label: 'No connection to server',
  isWarmup: true
};

export function getSignalLevelFromRtt(rttMs: number): SignalLevel {
  if (rttMs <= 100) return 4;
  if (rttMs <= 200) return 3;
  if (rttMs <= 350) return 2;
  return 1;
}

export function getSignalColor(_level: SignalLevel, quality: ConnectionQuality): string {
  if (quality === 'offline') return OFFLINE_SIGNAL_COLOR;
  if (quality === 'unstable') return UNSTABLE_SIGNAL_COLOR;
  if (quality === 'reconnecting') return RECONNECTING_SIGNAL_COLOR;
  return CONNECTED_SIGNAL_COLOR;
}

export function getSignalIcon(level: SignalLevel): SvgIconComponent {
  return SIGNAL_ICONS[level];
}

export function getConnectionQualityIcon(
  quality: ConnectionQuality,
  signalLevel: SignalLevel,
  reconnectingLevel?: SignalLevel
): SvgIconComponent {
  if (quality === 'offline') {
    return OFFLINE_SIGNAL_ICON;
  }

  if (quality === 'unstable') {
    return UNSTABLE_SIGNAL_ICON;
  }

  if (quality === 'reconnecting') {
    return getSignalIcon(reconnectingLevel ?? RECONNECTING_SIGNAL_LEVELS[0]);
  }

  return getSignalIcon(signalLevel);
}

/** Below bar level 1 — unstable, reconnecting, or offline. */
export function isPoorConnectionQuality(state: ConnectionQualityState): boolean {
  if (state.isWarmup) {
    return false;
  }

  return state.quality === 'unstable'
    || state.quality === 'reconnecting'
    || state.quality === 'offline';
}

export function getSignalLabel(
  quality: ConnectionQuality,
  signalLevel: SignalLevel,
  rttMs: number | null
): string {
  if (quality === 'offline') return 'No connection to server';
  if (quality === 'unstable') return 'Connection unstable — no response from server';
  if (quality === 'reconnecting') return 'Reconnecting…';
  if (rttMs == null) return 'Measuring connection…';

  const labels: Record<SignalLevel, string> = {
    4: `Excellent connection (${rttMs} ms)`,
    3: `Good connection (${rttMs} ms)`,
    2: `Fair connection (${rttMs} ms) — notifications may be delayed`,
    1: `Poor connection (${rttMs} ms) — check your internet`
  };
  return labels[signalLevel];
}

function buildOfflineState(isWarmup = true): ConnectionQualityState {
  return {
    quality: 'offline',
    signalLevel: 1,
    signalColor: OFFLINE_SIGNAL_COLOR,
    rttMs: null,
    lastMeasuredAt: null,
    label: 'No connection to server',
    isWarmup
  };
}

function buildReconnectingState(isWarmup = true): ConnectionQualityState {
  return {
    quality: 'reconnecting',
    signalLevel: 1,
    signalColor: RECONNECTING_SIGNAL_COLOR,
    rttMs: null,
    lastMeasuredAt: null,
    label: 'Reconnecting…',
    isWarmup
  };
}

function buildMeasuringState(): ConnectionQualityState {
  return {
    quality: 'reconnecting',
    signalLevel: 1,
    signalColor: RECONNECTING_SIGNAL_COLOR,
    rttMs: null,
    lastMeasuredAt: null,
    label: 'Measuring connection…',
    isWarmup: true
  };
}

function buildUnstableState(): ConnectionQualityState {
  return {
    quality: 'unstable',
    signalLevel: 1,
    signalColor: UNSTABLE_SIGNAL_COLOR,
    rttMs: null,
    lastMeasuredAt: Date.now(),
    label: 'Connection unstable — no response from server',
    isWarmup: false
  };
}

export class ConnectionQualityHelper {
  private consecutiveWorseCount = 0;
  private consecutiveBetterCount = 0;
  private consecutiveFailedPings = 0;
  private hasSuccessfulPing = false;
  private lastSignalLevel: SignalLevel = 3;
  private measurementEpoch = 0;
  private isMeasuring = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private onUpdate: ((state: ConnectionQualityState) => void) | null = null;
  private boundSocket: Socket | null = null;

  private readonly onConnect = () => {
    this.resetWarmup();
    this.resetHysteresis();
    this.onUpdate?.(buildMeasuringState());
    if (this.boundSocket) {
      this.startRttMonitor(this.boundSocket);
    }
  };

  private readonly onDisconnect = () => {
    this.stopRttMonitor();
    if (!this.boundSocket) {
      return;
    }
    const isWarmup = this.isPreEstablishmentWarmup();
    this.onUpdate?.(
      this.boundSocket.active ? buildReconnectingState(isWarmup) : buildOfflineState(isWarmup)
    );
  };

  private readonly onReconnectAttempt = () => {
    this.onUpdate?.(buildReconnectingState(this.isPreEstablishmentWarmup()));
  };

  private readonly onReconnect = () => {
    this.resetWarmup();
    this.resetHysteresis();
    this.onUpdate?.(buildMeasuringState());
    if (this.boundSocket) {
      this.startRttMonitor(this.boundSocket);
    }
  };

  private readonly onConnectError = () => {
    this.onUpdate?.(buildOfflineState(this.isPreEstablishmentWarmup()));
  };

  bind(socket: Socket, onUpdate: (state: ConnectionQualityState) => void): void {
    this.dispose(socket);

    this.boundSocket = socket;
    this.onUpdate = onUpdate;
    this.resetWarmup();

    socket.on('connect', this.onConnect);
    socket.on('disconnect', this.onDisconnect);
    socket.on('connect_error', this.onConnectError);
    socket.io.on('reconnect_attempt', this.onReconnectAttempt);
    socket.io.on('reconnect', this.onReconnect);

    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.stopRttMonitor();
        return;
      }
      if (socket.connected) {
        this.startRttMonitor(socket);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    if (socket.connected) {
      this.onConnect();
    } else if (socket.active) {
      onUpdate(buildReconnectingState());
    } else {
      onUpdate(buildMeasuringState());
    }
  }

  dispose(socket: Socket): void {
    this.stopRttMonitor();

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    socket.off('connect', this.onConnect);
    socket.off('disconnect', this.onDisconnect);
    socket.off('connect_error', this.onConnectError);
    socket.io.off('reconnect_attempt', this.onReconnectAttempt);
    socket.io.off('reconnect', this.onReconnect);

    this.boundSocket = null;
    this.onUpdate = null;
  }

  private isPreEstablishmentWarmup(): boolean {
    return !this.hasSuccessfulPing;
  }

  private resetWarmup(): void {
    this.hasSuccessfulPing = false;
    this.consecutiveFailedPings = 0;
  }

  private resetHysteresis(): void {
    this.consecutiveWorseCount = 0;
    this.consecutiveBetterCount = 0;
    this.lastSignalLevel = 3;
  }

  private applyHysteresis(candidate: SignalLevel): SignalLevel {
    if (candidate < this.lastSignalLevel) {
      this.consecutiveWorseCount += 1;
      this.consecutiveBetterCount = 0;
      if (this.consecutiveWorseCount >= 2) {
        this.lastSignalLevel = candidate;
        this.consecutiveWorseCount = 0;
      }
    } else if (candidate > this.lastSignalLevel) {
      this.consecutiveBetterCount += 1;
      this.consecutiveWorseCount = 0;
      if (this.consecutiveBetterCount >= 2) {
        this.lastSignalLevel = candidate;
        this.consecutiveBetterCount = 0;
      }
    } else {
      this.consecutiveWorseCount = 0;
      this.consecutiveBetterCount = 0;
    }

    return this.lastSignalLevel;
  }

  private buildStateFromRtt(rttMs: number): ConnectionQualityState {
    const rawLevel = getSignalLevelFromRtt(rttMs);
    const signalLevel = this.applyHysteresis(rawLevel);
    const quality: ConnectionQuality = signalLevel >= 3 ? 'good' : 'slow';

    return {
      quality,
      signalLevel,
      signalColor: getSignalColor(signalLevel, quality),
      rttMs,
      lastMeasuredAt: Date.now(),
      label: getSignalLabel(quality, signalLevel, rttMs),
      isWarmup: false
    };
  }

  private handleFailedPing(): void {
    this.consecutiveFailedPings += 1;

    if (!this.hasSuccessfulPing) {
      if (this.consecutiveFailedPings >= FAILED_PINGS_BEFORE_UNSTABLE) {
        this.onUpdate?.(buildUnstableState());
      } else {
        this.onUpdate?.(buildMeasuringState());
      }
      return;
    }

    this.onUpdate?.(buildUnstableState());
  }

  private measureRtt(socket: Socket): Promise<number | null> {
    return new Promise((resolve) => {
      if (!socket.connected) {
        resolve(null);
        return;
      }

      const sentAt = Date.now();
      const timeout = setTimeout(() => resolve(null), PING_TIMEOUT_MS);

      socket.emit('connection:ping', { sentAt }, () => {
        clearTimeout(timeout);
        resolve(Date.now() - sentAt);
      });
    });
  }

  private invalidateMeasurements(): void {
    this.measurementEpoch += 1;
    this.isMeasuring = false;
  }

  private startRttMonitor(socket: Socket): void {
    this.stopRttMonitor();
    const epoch = this.measurementEpoch;

    const runMeasurement = async () => {
      if (epoch !== this.measurementEpoch) {
        return;
      }

      if (document.visibilityState === 'hidden') {
        return;
      }

      if (this.isMeasuring) {
        return;
      }

      if (!socket.connected) {
        const isWarmup = this.isPreEstablishmentWarmup();
        this.onUpdate?.(
          socket.active ? buildReconnectingState(isWarmup) : buildOfflineState(isWarmup)
        );
        return;
      }

      this.isMeasuring = true;

      try {
        const rttMs = await this.measureRtt(socket);
        if (epoch !== this.measurementEpoch) {
          return;
        }

        if (rttMs === null) {
          this.handleFailedPing();
          return;
        }

        this.hasSuccessfulPing = true;
        this.consecutiveFailedPings = 0;
        this.onUpdate?.(this.buildStateFromRtt(rttMs));
      } finally {
        if (epoch === this.measurementEpoch) {
          this.isMeasuring = false;
        }
      }
    };

    void runMeasurement();
    this.pingTimer = setInterval(() => {
      void runMeasurement();
    }, PING_INTERVAL_MS);
  }

  private stopRttMonitor(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.invalidateMeasurements();
  }
}
